import {
  prefetchProfileBySlug,
  profileSlugResolveQueryOptions,
} from "@/lib/queries/profile";
import type { ProfileRouteContext } from "@/lib/types/route-context";
import { ensureOrgContext } from "@/lib/utils/org-context-guard";
import {
  ORG_CREATOR_SLUG_PREFIX,
  findProfileSlugRedirect,
} from "@/lib/utils/profile-slug-redirect";
import { createFileRoute, Outlet, redirect } from "@tanstack/react-router";
import type { OrgRouteContext } from "../route";

export const Route = createFileRoute("/(org)/o/$orgSlug/p/$profileSlug")({
  beforeLoad: async ({ context, params, location }) => {
    const orgContext = context as OrgRouteContext;
    const { profileSlug } = params;
    if (
      !ensureOrgContext(
        orgContext.organization,
        "(org)/o.$orgSlug/p.$profileSlug/route.tsx#beforeLoad",
        { orgSlug: params.orgSlug, profileSlug },
      )
    ) {
      throw redirect({ to: "/", search: { error: "organization_not_found" } });
    }

    let profile;
    let fetchError: Error | null = null;

    try {
      profile = await prefetchProfileBySlug(
        context.queryClient,
        orgContext.organization.id,
        profileSlug,
      );
    } catch (error) {
      fetchError = error as Error;
    }

    // Redirect on NOT_FOUND or missing profile with no other error
    const isNotFound =
      fetchError &&
      "data" in fetchError &&
      (fetchError as Error & { data?: { code?: string } }).data?.code ===
        "NOT_FOUND";

    if (isNotFound || (!profile && !fetchError)) {
      // A retired slug (EPD-11640): send the same task URL to the current slug
      // so links in old emails/bookmarks keep working.
      const slugRedirect = await findProfileSlugRedirect({
        location,
        prefix: ORG_CREATOR_SLUG_PREFIX,
        slug: profileSlug,
        resolve: (slug) =>
          context.queryClient.fetchQuery(
            profileSlugResolveQueryOptions(orgContext.organization.id, slug),
          ),
      });
      if (slugRedirect) {
        throw redirect({ href: slugRedirect, replace: true });
      }

      throw redirect({
        to: "/o/$orgSlug/projects",
        params: { orgSlug: params.orgSlug },
      });
    }

    // Rethrow non-404 errors
    if (fetchError) {
      throw fetchError;
    }

    return {
      profile,
    } as Omit<ProfileRouteContext, keyof OrgRouteContext>;
  },
  component: ProfileLayout,
});

function ProfileLayout() {
  return <Outlet />;
}
